import { StateCreator } from 'zustand';
import { IndexItem } from '../Index/store';
import { getAPIUrl, getHeaders, responseHandler } from '../utils';
import { PerformanceMetrics } from './store';
import { SelectorOption } from 'hooks/chartSelector';

const API_URL = getAPIUrl();
const DEFAULT_METRIC = 'price_return';

type YearlyItems = {
  [indexId: string]: {
    [year: string]: PerformanceMetrics[];
  };
};

export interface IndexAnalyticsSlice {
  items: {
    [indexId: string]: PerformanceMetrics[];
  };
  yearlyItems: YearlyItems;
  status: 'init' | 'loading' | 'done' | 'error';
  yearlyStatus: 'init' | 'loading' | 'done' | 'error';
  get: (index: IndexItem) => Promise<void>;
  getYearly: (index: IndexItem, years: string[]) => Promise<void>;
  update: (query: SelectorOption[], startDate?: string) => void;
  updateYearly: (query: SelectorOption[], years: string[]) => void;
}

export const createIndexAnalyticsSlice: StateCreator<
  IndexAnalyticsSlice,
  [],
  [],
  IndexAnalyticsSlice
> = (setState, getState) => ({
  items: {},
  yearlyItems: {},
  status: 'init',
  yearlyStatus: 'init',

  get: async (index: IndexItem) => {
    const startDate = index.baseDate ? `&start_date=${index.baseDate}` : '';
    const mainUrl = `${API_URL}/index/${index.id}/stats?metric=${
      index.plotMetric || DEFAULT_METRIC
    }${startDate}`;
    const analyticsStats = getState().items;
    const cached = Object.keys(analyticsStats);
    const mainCached = cached.includes(index.id);

    const related = (index.related || []).filter(
      d => d.defaultDisplay && d.id && !cached.includes(d.id)
    );

    const relatedUrls = related.map(r => {
      const url = `${API_URL}/${r.type === 'security' ? 'security/equity_legacy' : 'index'}/${
        r.id
      }/stats?metric=${r.metric || DEFAULT_METRIC}${startDate}`;

      return url;
    });

    const ids = mainCached ? related.map(r => r.id) : [index.id, ...related.map(r => r.id)];
    const urls = mainCached ? relatedUrls : [mainUrl, ...relatedUrls];

    if (!urls.length) {
      return;
    }
    setState({ status: 'loading' });
    const headers = await getHeaders('GET');

    const promises = urls.map(url =>
      responseHandler<{ results: PerformanceMetrics[] }>(
        fetch(url, headers),
        err => `Couldn't load Stats for ${index.name} (${err})`,
        true
      )
    );

    const responses = await Promise.all(promises);

    const newStats = responses.reduce((acc, { content }, idx) => {
      const current = ids[idx];
      return {
        ...acc,
        [current]: content?.results || [],
      };
    }, analyticsStats);

    setState({
      status: 'done',
      items: newStats,
    });
  },

  getYearly: async (index: IndexItem, years: string[]) => {
    if (!years.length) return;
    const related = (index.related || []).filter(d => d.defaultDisplay && d.id);

    console.log(related, years);

    const entities = [
      { id: index.id, type: 'index' as const, metric: index.plotMetric || DEFAULT_METRIC },
      ...related.map(r => ({
        id: r.id,
        type: r.type as 'index' | 'security',
        metric: r.metric || DEFAULT_METRIC,
      })),
    ];
    const requests: { id: string; year: string; url: string }[] = [];
    entities.forEach(({ id, type, metric }) => {
      years.forEach(year => {
        const dateParams = `&start_date=${Number(year) - 1}-12-31&end_date=${year}-12-31`;
        const base = type === 'security' ? 'security/equity_legacy' : 'index';
        requests.push({
          id,
          year,
          url: `${API_URL}/${base}/${id}/stats?metric=${metric}${dateParams}`,
        });
      });
    });
    setState({ yearlyStatus: 'loading' });
    const headers = await getHeaders('GET');
    const responses = await Promise.all(
      requests.map(({ id, year, url }) =>
        responseHandler<{ results: PerformanceMetrics[] }>(
          fetch(url, headers),
          err => `Couldn't load Stats for ${year} (${err})`,
          true
        ).then(res => ({ id, year, content: res.content?.results || [] }))
      )
    );
    const current = getState().yearlyItems;
    const next: YearlyItems = { ...current };
    responses.forEach(({ id, year, content }) => {
      if (!next[id]) next[id] = {};
      next[id][year] = content;
    });
    setState({ yearlyStatus: 'done', yearlyItems: next });
  },
  update: async (query: SelectorOption[], startDate?: string) => {
    const startDateParam = startDate ? `&start_date=${startDate}` : '';
    const analyticsStats = getState().items;

    console.log('Updating Index Analytics with query:', query, 'and startDate:', startDate);

    const cached = Object.keys(analyticsStats);
    const urls = query
      .filter(q => !cached.includes(q.value))
      .map(r => {
        const url = `${API_URL}/${r.type === 'security' ? 'security/equity_legacy' : 'index'}/${
          r.value
        }/stats?metric=${r.metric || DEFAULT_METRIC}${startDateParam}`;

        return url;
      });

    if (!urls.length) {
      return;
    }
    setState({ status: 'loading' });
    const headers = await getHeaders('GET');

    const promises = urls.map((url, i) =>
      responseHandler<{ results: PerformanceMetrics[] }>(
        fetch(url, headers),
        err => `Couldn't load Analytics for ${query[i].label} (${err})`,
        true
      )
    );

    const responses = await Promise.all(promises);

    const newStats = responses.reduce((acc, { content }, idx) => {
      const current = query[idx].value;
      return {
        ...acc,
        [current]: content?.results || [],
      };
    }, analyticsStats);

    setState({
      status: 'done',
      items: newStats,
    });
  },
  updateYearly: async (query: SelectorOption[], years: string[]) => {
    if (!years.length) return;
    const entities = query.map(q => ({
      id: q.value,
      type: q.type as 'index' | 'security',
      metric: q.metric,
    }));
    const requests: { id: string; year: string; url: string }[] = [];
    entities.forEach(({ id, type, metric }) => {
      years.forEach(year => {
        const dateParams = `&start_date=${year}-01-01&end_date=${year}-12-31`;
        const base = type === 'security' ? 'security/equity_legacy' : 'index';
        requests.push({
          id,
          year,
          url: `${API_URL}/${base}/${id}/stats?metric=${metric || DEFAULT_METRIC}${dateParams}`,
        });
      });
    });
    setState({ yearlyStatus: 'loading' });
    const headers = await getHeaders('GET');
    const responses = await Promise.all(
      requests.map(({ id, year, url }) =>
        responseHandler<{ results: PerformanceMetrics[] }>(
          fetch(url, headers),
          err => `Couldn't load Analytics for ${year} (${err})`,
          true
        ).then(res => ({ id, year, content: res.content?.results || [] }))
      )
    );
    const current = getState().yearlyItems;
    const next: YearlyItems = { ...current };
    responses.forEach(({ id, year, content }) => {
      if (!next[id]) next[id] = {};
      next[id][year] = content;
    });
    setState({ yearlyStatus: 'done', yearlyItems: next });
  },
});

export default createIndexAnalyticsSlice;
